import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { authFetch } from '../utils/api';
import { logoutUser } from './authSlice';

// ─── Async Thunks ──────────────────────────────────────────────────────────────

export const fetchUserData = createAsyncThunk(
  'userData/fetch',
  async (_, { rejectWithValue }) => {
    try {
      const [favRes, shinyRes] = await Promise.all([
        authFetch('/user/favorites'),
        authFetch('/user/shinies'),
      ]);
      const [favData, shinyData] = await Promise.all([
        favRes.json(),
        shinyRes.json(),
      ]);
      return {
        favorites: favData.favorites || [],
        shinies: shinyData.shinies || [],
      };
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const addFavorite = createAsyncThunk(
  'userData/addFavorite',
  async ({ pokedexId, name }, { rejectWithValue }) => {
    try {
      const res = await authFetch('/user/favorites', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pokedexId, name }),
      });
      if (!res.ok && res.status !== 409) throw new Error('Failed to add favorite');
      return { pokedexId, name };
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const removeFavorite = createAsyncThunk(
  'userData/removeFavorite',
  async (pokedexId, { rejectWithValue }) => {
    try {
      const res = await authFetch(`/user/favorites/${pokedexId}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Failed to remove favorite');
      return pokedexId;
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const addShiny = createAsyncThunk(
  'userData/addShiny',
  async ({ pokedexId, name }, { rejectWithValue }) => {
    try {
      const res = await authFetch('/user/shinies', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pokedexId, name }),
      });
      if (!res.ok && res.status !== 409) throw new Error('Failed to add shiny');
      return { pokedexId, name };
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const removeShiny = createAsyncThunk(
  'userData/removeShiny',
  async (pokedexId, { rejectWithValue }) => {
    try {
      const res = await authFetch(`/user/shinies/${pokedexId}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Failed to remove shiny');
      return pokedexId;
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

// ─── Slice ─────────────────────────────────────────────────────────────────────

const userDataSlice = createSlice({
  name: 'userData',
  initialState: {
    favorites: [],
    shinies: [],
    loaded: false,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserData.fulfilled, (state, action) => {
        state.favorites = action.payload.favorites;
        state.shinies = action.payload.shinies;
        state.loaded = true;
      })
      .addCase(addFavorite.fulfilled, (state, action) => {
        if (!state.favorites.some(f => f.pokedexId === action.payload.pokedexId)) {
          state.favorites.push({ ...action.payload, addedAt: new Date().toISOString() });
        }
      })
      .addCase(removeFavorite.fulfilled, (state, action) => {
        state.favorites = state.favorites.filter(f => f.pokedexId !== action.payload);
      })
      .addCase(addShiny.fulfilled, (state, action) => {
        if (!state.shinies.some(s => s.pokedexId === action.payload.pokedexId)) {
          state.shinies.push({ ...action.payload, caughtAt: new Date().toISOString() });
        }
      })
      .addCase(removeShiny.fulfilled, (state, action) => {
        state.shinies = state.shinies.filter(s => s.pokedexId !== action.payload);
      })
      // Clear when user logs out
      .addCase(logoutUser.fulfilled, (state) => {
        state.favorites = [];
        state.shinies = [];
        state.loaded = false;
      });
  },
});

// ─── Selectors ─────────────────────────────────────────────────────────────────

export const selectFavorites = (state) => state.userData.favorites;
export const selectShinies = (state) => state.userData.shinies;
export const selectUserDataLoaded = (state) => state.userData.loaded;

// Factory selectors — use as: useSelector(selectIsFavorite(pokedexId))
export const selectIsFavorite = (pokedexId) => (state) =>
  state.userData.favorites.some(f => f.pokedexId === pokedexId);
export const selectIsShiny = (pokedexId) => (state) =>
  state.userData.shinies.some(s => s.pokedexId === pokedexId);

export default userDataSlice.reducer;
